import { useCallback, useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  dataDogLogger,
  DataDogLoggerActionTypes,
} from 'commons/utils/dataDogLogger';
import {
  AipriseEventCallback,
  setupAipriseEventHandlers,
} from './setupAipriseEventHandlers';

interface UseAipriseVerificationProps {
  onFinish: AipriseEventCallback;
  onExit: AipriseEventCallback;
  legalEntityType?: string;
}

const ACTION_OWNER = 'AipriseVerification';

export function useAipriseVerification({
  onFinish,
  onExit,
  legalEntityType,
}: UseAipriseVerificationProps) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSdk = async () => {
      try {
        await import('aiprise-web-sdk');
        if (!cancelled) {
          setSdkReady(true);
          setSdkError(null);
        }
      } catch (error) {
        const normalized =
          error instanceof Error ? error : new Error(String(error));

        datadogRum.addAction('aiprise_sdk_load_error', {
          error: normalized.message,
        });
        dataDogLogger.generate({
          actionMessage: 'AIPrise SDK failed to load',
          actionOwner: ACTION_OWNER,
          actionType: DataDogLoggerActionTypes.Error,
          actionData: { error: normalized.message },
        });

        if (!cancelled) {
          setSdkError(normalized);
        }
      }
    };

    loadSdk();

    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const retryLoad = useCallback(() => {
    setSdkError(null);
    setSdkReady(false);
    setLoadAttempt((attempt) => attempt + 1);
  }, []);

  const elementRef = useCallback(
    (node: HTMLElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      if (node && sdkReady) {
        cleanupRef.current = setupAipriseEventHandlers(
          node,
          onFinish,
          onExit,
          legalEntityType,
        );
      }
    },
    [sdkReady, onFinish, onExit, legalEntityType],
  );

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  return {
    sdkReady,
    sdkError,
    retryLoad,
    elementRef,
  };
}
